import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  createGame, deobfuscate, isLanguage, isSupportedLength, loadInsightsFor,
  loadLengthAccepting, obfuscate, parseFragmentLanguage, parseFragmentWord,
  type GameState, type Language,
} from '@lexicon/core';
import { HexagonIcon } from '../components/icons';
import { getChallenge, postChallengeResult } from '../lib/api';
import { navigate } from '../lib/router';
import { loadSession, toGameState, type GameResult } from '../game/session';
import { useApp, useStrings } from '../state/AppContext';
import { GamePlay } from './GamePlay';

/**
 * The fragment carries the word, but its length decides which accepted-
 * guess list to load — so the shape check happens here and the dictionary
 * check happens after the load, in `prepareWord`.
 */
const ANY_SHAPE_VALID = () => true;

/** The word list a challenge is played against, and the language it is. */
interface WordData {
  language: Language;
  isValid: (word: string) => boolean;
}

/**
 * Everything a challenge board needs: the accepted-guess check the game
 * plays with, the language that check came from, and (best-effort) that
 * language's insight chunk so the result sheet can show the answer's
 * definition. Null means "cannot play this" — an unplayable length, a
 * word no language accepts, or a chunk that would not load.
 *
 * A challenge's language is not the player's: the link may come from
 * someone playing the other one. `hint` is where to look first — the
 * `lang` the worker stored, or the fragment's `l=` for an offline link,
 * falling back to the player's own language — and `loadLengthAccepting`
 * settles it by finding the list that actually contains the word, which
 * is also how a challenge created before the field existed still resolves.
 */
async function prepareWord(word: string | null, hint: Language): Promise<WordData | null> {
  if (word === null || !isSupportedLength(word.length)) return null;
  try {
    const found = await loadLengthAccepting(word, hint);
    if (found === null) return null;
    // Insights are a nicety (spec §3.7): a failed chunk means "no
    // dictionary entry", never a failed boot.
    await loadInsightsFor(word.length, found.language).catch(() => undefined);
    return { language: found.language, isValid: found.data.isValid };
  } catch {
    return null;
  }
}

type Boot =
  | { phase: 'loading' }
  | { phase: 'error'; message: string; retryable: boolean }
  | {
    phase: 'ready';
    /**
     * Storage/GamePlay identity for this boot. Equals `code` for a real
     * server-issued code. For the degraded-link literal `code === 'offline'`
     * (every fragment-only link shares that one route param — Task 13),
     * this is `offline:<obfuscate(word)>` instead, so two different offline
     * links on the same device don't collide on one `lexicon.challengeGame.
     * offline` session and clobber each other's board/answer/result.
     */
    sessionId: string;
    game: GameState;
    result: GameResult | null;
    /** Words already paid for in a restored session (spec §3.7 / I4). */
    revealedInsights: readonly string[];
    challengerName?: string;
    offline: boolean;
    /** Accepted-guess check for this challenge's length (spec §10.5). */
    isValid: (word: string) => boolean;
    /** The language the challenge word belongs to — not necessarily the app's. */
    wordLanguage: Language;
  };

type SendState = 'idle' | 'sending' | 'sent' | 'failed' | 'offline';

function ChallengeBanner({ code, challengerName, offline, action }: {
  code: string; challengerName?: string; offline: boolean; action: ReactNode;
}) {
  const t = useStrings();
  return (
    <section className="glass overflow-hidden rounded-2xl" aria-label={t.challenge.sectionLabel}>
      <div className="flex items-center gap-2 px-4 py-3">
        <HexagonIcon className="h-4 w-4 shrink-0 text-accent" />
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold tracking-[0.18em]">
          {t.challenge.label}
        </span>
        <span className="shrink-0 text-dim" aria-hidden="true">|</span>
        {/* min-w-0 lets this shrink below its content width so a long
            challenger name truncates with an ellipsis instead of wrapping
            the row onto two lines (I4). */}
        <span className="min-w-0 flex-1 truncate text-sm text-dim">
          {challengerName ? t.challenge.from(challengerName) : t.challenge.code(code)}
        </span>
        {offline ? (
          <span className="shrink-0 whitespace-nowrap rounded-full border border-hairline px-2 py-0.5 text-[10px] text-dim">
            {t.challenge.offline}
          </span>
        ) : null}
        {action ? <div className="ml-auto shrink-0 whitespace-nowrap">{action}</div> : null}
      </div>
    </section>
  );
}

export function ChallengePlayScreen({ code, hash }: { code: string; hash: string }) {
  const { ready, storage, deviceId, playerName, language, t } = useApp();
  const [boot, setBoot] = useState<Boot>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [sendState, setSendState] = useState<SendState>('idle');

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    setBoot({ phase: 'loading' });

    void (async () => {
      // Read the fragment once, up front — never sent to the API, but
      // needed now (not just as a later fallback) to compute a collision-
      // free storage id for the 'offline' degraded-link case below. It
      // arrives as a prop so that following a second offline link (same
      // path, same route param, different word) actually re-boots (F1).
      const fragmentWord = parseFragmentWord(hash, ANY_SHAPE_VALID);
      // Where to look for the word list first. The fragment's tag when the
      // link carries one, otherwise the language being played; the server's
      // `lang` overrides it below for a code that resolves.
      const fragmentLanguage = parseFragmentLanguage(hash);

      let sessionId: string;
      if (code === 'offline') {
        if (!fragmentWord) {
          setBoot({ phase: 'error', message: t.challenge.incompleteLink, retryable: false });
          return;
        }
        sessionId = `offline:${obfuscate(fragmentWord)}`;
      } else {
        sessionId = code;
      }

      const session = await loadSession(storage, 'challenge', sessionId);
      if (cancelled) return;
      if (session) {
        // A stored session's answer already passed isSession's 4–7 shape
        // check; all that is missing is that length's word list. Its own
        // dictionary membership is deliberately not re-checked — a list
        // that changed between versions must not lock a player out of a
        // game in progress.
        const words = await prepareWord(session.answer, fragmentLanguage ?? language);
        if (cancelled) return;
        if (!words) {
          setBoot({ phase: 'error', message: t.challenge.loadFailed, retryable: true });
          return;
        }
        setBoot({
          phase: 'ready', sessionId, game: toGameState(session), result: session.result,
          revealedInsights: session.revealedInsights ?? [], offline: code === 'offline',
          isValid: words.isValid, wordLanguage: words.language,
        });
        return;
      }

      const fallback = async (message: string, retryable: boolean, hint: Language = fragmentLanguage ?? language) => {
        // No usable word to fall back to at all — the caller's message
        // (link malformed / expired / incomplete) is the right one, and
        // it is never retryable: there is nothing a retry could fix.
        if (!fragmentWord) {
          setBoot({ phase: 'error', message, retryable });
          return;
        }
        const words = await prepareWord(fragmentWord, hint);
        if (cancelled) return;
        if (words) {
          setBoot({
            phase: 'ready', sessionId, game: createGame(fragmentWord, 'challenge'),
            result: null, revealedInsights: [], offline: true,
            isValid: words.isValid, wordLanguage: words.language,
          });
          return;
        }
        // The fragment itself is fine (right shape, present) — only its
        // word list failed to load. That is a transient failure like the
        // server-code path's, not a malformed/expired link, so it gets the
        // same retryable copy (F1 review fix) instead of the caller's
        // "incomplete/expired" message.
        setBoot({ phase: 'error', message: t.challenge.loadFailed, retryable: true });
      };

      if (code === 'offline') {
        await fallback(t.challenge.incompleteLink, false);
        return;
      }

      const res = await getChallenge(code);
      if (cancelled) return;

      if (res.ok) {
        const word = deobfuscate(res.value.word);
        // The creator's own language, as the worker recorded it. An older
        // worker omits it; the fragment, then the player's language, then
        // the word itself decide instead.
        const served = res.value.lang;
        const hint = isLanguage(served) ? served : fragmentLanguage ?? language;
        const words = await prepareWord(word, hint);
        if (cancelled) return;
        if (word && words) {
          setBoot({
            phase: 'ready', sessionId, game: createGame(word, 'challenge'), result: null,
            revealedInsights: [], offline: false,
            isValid: words.isValid, wordLanguage: words.language,
            ...(res.value.challengerName ? { challengerName: res.value.challengerName } : {}),
          });
          return;
        }
        await fallback(t.challenge.loadFailed, true, hint);
        return;
      }

      if (res.error === 'not-found') {
        await fallback(t.challenge.expiredLink, false);
        return;
      }
      await fallback(t.challenge.loadFailed, true);
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, storage, code, hash, attempt, language, t]);

  const offline = boot.phase === 'ready' && boot.offline;
  // `useGame`'s onFinished (and so GamePlay's onFinalized) fires on every
  // mount of an already-finished game, not just a live finish (documented
  // contract in game/useGame.ts). Without this guard, reopening a finished
  // challenge link re-posts its result on every visit. A game that was
  // already finished when this boot loaded it (a restored session with a
  // stored result) must never post again; only a finish that happens live,
  // during this visit (boot.result === null), does.
  const alreadyFinishedAtBoot = boot.phase === 'ready' && boot.result !== null;

  const handleFinalized = useCallback((result: GameResult) => {
    if (alreadyFinishedAtBoot) return;
    if (offline) {
      setSendState('offline');
      return;
    }
    setSendState('sending');
    void (async () => {
      const sent = await postChallengeResult(code, {
        deviceId,
        ...(playerName ? { playerName } : {}),
        won: result.won,
        guessCount: result.guessCount,
      });
      setSendState(sent ? 'sent' : 'failed');
    })();
  }, [alreadyFinishedAtBoot, offline, code, deviceId, playerName]);

  if (boot.phase === 'loading') {
    return <p className="p-6 text-center text-dim">{t.challenge.loading}</p>;
  }

  if (boot.phase === 'error') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p role="alert" className="text-sm text-danger">{boot.message}</p>
        {boot.retryable ? (
          <button
            type="button"
            className="min-h-[44px] rounded-xl border border-hairline px-5 text-sm font-semibold"
            onClick={() => setAttempt((n) => n + 1)}
          >
            {t.common.tryAgain}
          </button>
        ) : null}
        <button
          type="button"
          className="min-h-[44px] rounded-xl bg-grad-accent px-5 text-sm font-semibold text-white"
          onClick={() => navigate('/')}
        >
          {t.common.backToToday}
        </button>
      </div>
    );
  }

  const footerText = sendState === 'sending' ? t.challenge.sending
    : sendState === 'sent'
      ? (playerName ? t.challenge.sentAs(playerName) : t.challenge.sentAnonymously)
      : sendState === 'failed' ? t.challenge.sendFailed
        : sendState === 'offline' ? t.challenge.offlineNotSent
          : null;

  return (
    <GamePlay
      key={boot.sessionId}
      id={boot.sessionId}
      initial={boot.game}
      isValid={boot.isValid}
      initialResult={boot.result}
      initialRevealedInsights={boot.revealedInsights}
      shareId={code}
      wordLanguage={boot.wordLanguage}
      ctaLabel={t.challenge.createYourOwn}
      onCta={() => navigate('/challenge/new')}
      onFinalized={handleFinalized}
      resultFooter={footerText ? <p className="text-center text-xs text-dim">{footerText}</p> : null}
      banner={({ finished, openResult }) => (
        <ChallengeBanner
          code={code}
          {...(boot.challengerName ? { challengerName: boot.challengerName } : {})}
          offline={boot.offline}
          action={finished ? (
            <button
              type="button"
              className="min-h-[44px] whitespace-nowrap text-sm text-dim"
              onClick={openResult}
            >
              {t.common.viewResult}
            </button>
          ) : null}
        />
      )}
    />
  );
}
